import { useState, useEffect } from 'react';
import { vatAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus, 
  Plus, Trash2, Edit2, X, Check, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const VATDashboard = () => {
  const [projection, setProjection] = useState(null);
  const [historical, setHistorical] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    taxable_amount: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projRes, histRes] = await Promise.all([
        vatAPI.getProjection(),
        vatAPI.getHistorical()
      ]);
      setProjection(projRes.data);
      setHistorical(histRes.data);
    } catch (error) {
      toast.error('Failed to load VAT data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async () => {
    if (!formData.taxable_amount && formData.taxable_amount !== 0) {
      toast.error('Monthly income is required');
      return;
    }

    try {
      await vatAPI.addHistoricalEntry({
        year: parseInt(formData.year),
        month: parseInt(formData.month),
        taxable_amount: parseFloat(formData.taxable_amount) || 0,
        notes: formData.notes
      });
      toast.success('Entry added successfully');
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const handleUpdateEntry = async () => {
    try {
      await vatAPI.updateHistoricalEntry(editingEntry.id, {
        year: parseInt(formData.year),
        month: parseInt(formData.month),
        taxable_amount: parseFloat(formData.taxable_amount) || 0,
        notes: formData.notes
      });
      toast.success('Entry updated');
      setEditingEntry(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await vatAPI.deleteHistoricalEntry(id);
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const resetForm = () => {
    setFormData({
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      taxable_amount: '',
      notes: ''
    });
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      year: entry.year,
      month: entry.month,
      taxable_amount: entry.taxable_amount,
      notes: entry.notes || ''
    });
    setShowAddModal(true);
  };

  const getTrendIcon = (direction) => {
    if (direction === 'increasing') return <TrendingUp className="w-4 h-4 text-amber-500" />;
    if (direction === 'decreasing') return <TrendingDown className="w-4 h-4 text-emerald-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Prepare chart data
  const chartData = projection?.monthly_breakdown?.map(m => ({
    period: `${MONTHS[m.month - 1]} ${m.year}`,
    taxable: m.taxable,
    threshold: 90000 / 12 // Monthly average threshold line
  })) || [];

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-[#111] rounded-xl animate-pulse" />
        <div className="h-64 bg-[#111] rounded-xl animate-pulse" />
      </div>
    );
  }

  const isOverThreshold = projection?.rolling_12m_total >= 90000;
  const isNearThreshold = projection?.pct_of_threshold >= 80;

  return (
    <div className="space-y-6">
      {/* Main Stats Card */}
      <div className={`bg-[#111] border rounded-xl p-6 ${
        isOverThreshold ? 'border-red-500/50 bg-red-500/5' :
        isNearThreshold ? 'border-amber-500/50 bg-amber-500/5' :
        'border-[#333]'
      }`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              VAT Threshold Monitor
              {isOverThreshold && <AlertTriangle className="w-5 h-5 text-red-500" />}
              {!isOverThreshold && projection?.next_30_day_breach_flag && (
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              )}
            </h3>
            <p className="text-sm text-white/60">UK VAT Registration Threshold: £90,000</p>
          </div>
          <div className="flex items-center gap-2">
            {getTrendIcon(projection?.trend_direction)}
            <span className="text-sm text-white/60 capitalize">{projection?.trend_direction}</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          {/* Rolling 12 Month Total */}
          <div className="bg-[#0a0a0a] rounded-lg p-4">
            <div className="text-sm text-white/60 mb-1">Rolling 12M Taxable</div>
            <div className={`text-2xl font-bold ${
              isOverThreshold ? 'text-red-400' : 'text-white'
            }`}>
              {formatCurrency(projection?.rolling_12m_total || 0)}
            </div>
            <div className="text-sm text-white/40">
              {projection?.pct_of_threshold?.toFixed(1)}% of threshold
            </div>
          </div>

          {/* Headroom / Overage */}
          <div className="bg-[#0a0a0a] rounded-lg p-4">
            <div className="text-sm text-white/60 mb-1">
              {isOverThreshold ? 'Over Threshold By' : 'Headroom Remaining'}
            </div>
            <div className={`text-2xl font-bold ${
              isOverThreshold ? 'text-red-400' : 'text-emerald-400'
            }`}>
              {formatCurrency(Math.abs(90000 - (projection?.rolling_12m_total || 0)))}
            </div>
            {projection?.projected_crossing_date && !isOverThreshold && (
              <div className="text-sm text-amber-400">
                Est. breach: {projection.projected_crossing_date}
              </div>
            )}
          </div>

          {/* Monthly Average */}
          <div className="bg-[#0a0a0a] rounded-lg p-4">
            <div className="text-sm text-white/60 mb-1">Avg Monthly Taxable</div>
            <div className="text-2xl font-bold text-white">
              {formatCurrency(projection?.average_monthly_taxable || 0)}
            </div>
            <div className="text-sm text-white/40">
              Based on active months
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-white/60 mb-1">
            <span>Progress to Threshold</span>
            <span>{projection?.pct_of_threshold?.toFixed(1)}%</span>
          </div>
          <div className="h-3 bg-[#0a0a0a] rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 rounded-full ${
                isOverThreshold ? 'bg-red-500' :
                isNearThreshold ? 'bg-amber-500' :
                'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(projection?.pct_of_threshold || 0, 100)}%` }}
            />
          </div>
        </div>

        {/* Warnings */}
        {isOverThreshold && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300">
            <strong>VAT Registration Required!</strong> Your taxable turnover has exceeded the £90,000 threshold. 
            You must register for VAT within 30 days.
          </div>
        )}
        {!isOverThreshold && projection?.next_30_day_breach_flag && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-300">
            <strong>Warning:</strong> At current rates, you may exceed the VAT threshold within the next 30 days.
            {projection?.days_until_breach && (
              <span> Estimated {projection.days_until_breach} days until breach.</span>
            )}
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="bg-[#111] border border-[#333] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Monthly Taxable Turnover</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="period" 
                tick={{ fill: '#999', fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                tick={{ fill: '#999', fontSize: 11 }}
                tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`}
              />
              <Tooltip 
                contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
                formatter={(value) => [formatCurrency(value), 'Taxable']}
              />
              <ReferenceLine 
                y={7500} 
                stroke="#ef4444" 
                strokeDasharray="5 5"
                label={{ value: '£90k/yr avg', fill: '#ef4444', fontSize: 10 }}
              />
              <Line 
                type="monotone" 
                dataKey="taxable" 
                stroke="#a855f7" 
                strokeWidth={2}
                dot={{ fill: '#a855f7', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Historical Entries Management */}
      <div className="bg-[#111] border border-[#333] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Historical Entries</h3>
            <p className="text-sm text-white/60">Add previous years' financial data</p>
          </div>
          <Button 
            onClick={() => {
              resetForm();
              setEditingEntry(null);
              setShowAddModal(true);
            }}
            className="bg-[#FF007F] hover:bg-[#D6006A]"
            data-testid="add-historical-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </Button>
        </div>

        {historical.length === 0 ? (
          <div className="text-center py-8 text-white/40">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No historical entries yet.</p>
            <p className="text-sm">Add your previous months' income to get accurate projections.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#333]">
                  <th className="text-left py-3 px-2 text-white/60 text-sm font-medium">Period</th>
                  <th className="text-right py-3 px-2 text-white/60 text-sm font-medium">Monthly Income</th>
                  <th className="text-left py-3 px-2 text-white/60 text-sm font-medium">Notes</th>
                  <th className="py-3 px-2 text-white/60 text-sm font-medium text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {historical.map((entry) => (
                  <tr key={entry.id} className="border-b border-[#222] hover:bg-[#0a0a0a]">
                    <td className="py-3 px-2 text-white">
                      {MONTHS[entry.month - 1]} {entry.year}
                    </td>
                    <td className="py-3 px-2 text-right text-emerald-400 font-medium">
                      {formatCurrency(entry.taxable_amount)}
                    </td>
                    <td className="py-3 px-2 text-white/60 text-sm max-w-[200px] truncate">
                      {entry.notes || '-'}
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => startEdit(entry)}
                          className="p-1.5 rounded hover:bg-[#333] text-blue-400"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="p-1.5 rounded hover:bg-[#333] text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111] border border-[#333] rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                {editingEntry ? 'Edit Entry' : 'Add Historical Entry'}
              </h3>
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setEditingEntry(null);
                  resetForm();
                }}
                className="p-1 hover:bg-[#333] rounded"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/60 mb-1">Year</label>
                  <select
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-white"
                  >
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-1">Month</label>
                  <select
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                    className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-white"
                  >
                    {MONTHS.map((month, idx) => (
                      <option key={idx} value={idx + 1}>{month}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-1">Monthly Income (£) *</label>
                <input
                  type="number"
                  value={formData.taxable_amount}
                  onChange={(e) => setFormData({ ...formData, taxable_amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-white text-lg"
                  data-testid="taxable-amount-input"
                />
                <p className="text-xs text-white/40 mt-1">Total income for this month</p>
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="e.g. Summer term"
                  className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingEntry(null);
                  resetForm();
                }}
                className="flex-1 border-[#333] text-white hover:bg-[#222]"
              >
                Cancel
              </Button>
              <Button
                onClick={editingEntry ? handleUpdateEntry : handleAddEntry}
                className="flex-1 bg-[#FF007F] hover:bg-[#D6006A]"
                data-testid="save-entry-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                {editingEntry ? 'Update' : 'Add Entry'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VATDashboard;
